import { Component, ViewChild } from "@angular/core";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { MatPaginator } from "@angular/material/paginator";
import { EducationService } from "../services/education/education.service";
import { ERTEducation } from "../interfaces/ert-education/ert-education";
import { BrowserModule } from "@angular/platform-browser";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  FilterCriteria,
  FilterService,
} from "../services/common/filter.service";
import { LoggedUserService } from "../services/common/logged-user.service";

@Component({
  selector: "ert-education-component",
  templateUrl: "./ert-education.component.html",
  imports: [BrowserModule],
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }
    `,
  ],
})
export class ERTEducationComponent {
  title = "Education";
  totalItems: number;
  sortedBy: string;
  filterKey: string;
  isLoading = false;
  dataSource = new MatTableDataSource<ERTEducation>();
  pageSize = 10;
  searchForm: FormGroup;

  facilityId: number;

  // Filter properties
  activeFilters: FilterCriteria[] = [];
  showFilterPanel = false;

  // Storage keys - use unique keys for this component
  readonly ert_education_filters_storage_key = "ert-education-filters";
  readonly ert_education_saved_filters_storage_key =
    "ert-education-saved-filters";

  displayedColumns: Map<string, string> = new Map<string, string>([
    ["id", "Id"],
    ["userErtName", "Employee Name"],
    ["crew", "Crew"],
    ["schoolAttended", "School Attended"],
    ["schoolDivision", "School Division"],
    ["attendanceDate", "Attendance Date"],
    ["hours", "Hours"],
    ["onOffSite", "On/Off Site"],
    ["comments", "Comments"],
    ["recertDate", "Recert Date"],
    ["registrationNumber", "Registration Number"],
    ["isQualifiedForErtRoster", "Is Qualified"],
    ["actions", "actions"],
  ]);
  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private service: EducationService,
    private router: Router,
    private formBuilder: FormBuilder,
    private filterService: FilterService,
    private loggedUserService: LoggedUserService
  ) {}

  onSortChange(sortState: Sort): void {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${this.displayedColumns.get(
        sortState.active
      )}`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  navigateToNewEntry() {
    this.router.navigate(["/ert-education/new-entry"]);
  }

  editEducation(element: ERTEducation) {
    this.router.navigate(["/ert-education/ert-education-view"], {
      queryParams: { id: element.id },
    });
  }

  ngOnInit(): void {
    this.initSearchForm();
    this.loadEducations();

    // Initialize filter service
    this.filterService.loadActiveFiltersFromStorage(
      this.ert_education_filters_storage_key
    );
    this.filterService.loadSavedFiltersFromStorage(
      this.ert_education_saved_filters_storage_key
    );

    // Subscribe to active filters changes
    this.filterService
      .getActiveFilters(this.ert_education_filters_storage_key)
      .subscribe((filters) => {
        this.activeFilters = filters;
        if (this.dataSource) {
          this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
        }
      });
  }

  initSearchForm(): void {
    this.searchForm = this.formBuilder.group({
      searchTerm: [""],
    });
    this.searchForm.get("searchTerm")?.valueChanges.subscribe((value) => {
      this.applyFilter(value);
    });
  }

  applyFilter(filterValue: string): void {
    // Create custom filter predicate that combines search term with advanced filters
    this.dataSource.filterPredicate = (
      data: ERTEducation,
      filterString: string
    ) => {
      // Parse the filter string - format is "searchTerm||activeFilters"
      const parts = filterString.split("||");
      const searchTerm = parts[0].toLowerCase();
      let activeFilters: FilterCriteria[] = [];

      try {
        if (parts[1]) {
          activeFilters = JSON.parse(parts[1]);
        }
      } catch (error) {
        console.error("Error parsing active filters:", error);
      }

      // Basic search
      const searchMatch = searchTerm
        ? (data.schoolAttended?.toLowerCase() || "").includes(searchTerm) ||
          (data.schoolDivision?.toLowerCase() || "").includes(searchTerm) ||
          (data.crew?.toLowerCase() || "").includes(searchTerm) ||
          (data.comments?.toLowerCase() || "").includes(searchTerm) ||
          (data.registrationNumber?.toLowerCase() || "").includes(searchTerm)
        : true; // If no search term, consider it a match

      // Advanced filters match
      let advancedFiltersMatch = true;
      if (activeFilters.length > 0) {
        advancedFiltersMatch = activeFilters.every((filter) => {
          const value = this.getPropertyValue(data, filter.column);

          if (value === null || value === undefined) {
            return false;
          }

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && advancedFiltersMatch;
    };

    // Combine search term and active filters
    const combinedFilter =
      filterValue.trim().toLowerCase() +
      "||" +
      JSON.stringify(this.activeFilters);
    this.dataSource.filter = combinedFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.totalItems = this.dataSource.filteredData.length;
  }

  // Helper to get property value for filtering
  getPropertyValue(data: ERTEducation, property: string): any {
    switch (property) {
      case "id":
        return data.id;
      case "crew":
        return data.crew;
      case "schoolAttended":
        return data.schoolAttended;
      case "schoolDivision":
        return data.schoolDivision;
      case "attendanceDate":
        return data.attendanceDate;
      case "hours":
        return data.hours;
      case "onOffSite":
        return data.onOffSite === 1
          ? "On Site"
          : data.onOffSite === 2
          ? "Off Site"
          : "Unknown";
      case "comments":
        return data.comments;
      case "recertDate":
        return data.recertDate;
      case "registrationNumber":
        return data.registrationNumber;
      case "isQualifiedForErtRoster":
        return data.isQualifiedForErtRoster ? "Yes" : "No";
      default:
        return "";
    }
  }

  // Toggle filter panel
  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  // Handler for the filtersChanged event from advanced-filter-panel component
  onFiltersChanged(filters: FilterCriteria[]): void {
    // Filter service already updates the activeFilters array,
    // we just need to trigger a filter
    this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
  }

  // Clear all filters
  clearAllFilters() {
    this.filterService.clearAllFilters(this.ert_education_filters_storage_key);
    this.searchForm.patchValue({ searchTerm: "" });
    this.applyFilter("");
  }

  // Get column display name for UI
  getColumnDisplayName(columnKey: string): string {
    return this.displayedColumns.get(columnKey) || columnKey;
  }

  loadEducations() {
    this.isLoading = true;
    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.facilityId = Number(user.facilityId) || 0;
      this.service.getEducation(this.facilityId).subscribe((data) => {
        this.dataSource.data = data;
        this.totalItems = data.length;
        setTimeout(() => {
          this.dataSource.sort = this.sort;
          this.dataSource.paginator = this.paginator;
          this.sort.sort({ id: "id", start: "asc" } as MatSortable);
          this.sort.sortChange.subscribe((sortState: Sort) => {
            this.onSortChange(sortState);
          });
          this.sortedBy = `Sorted by ${this.displayedColumns.get("id")}`;
        }, 10);
        this.isLoading = false;
      });
    });
  }

  deleteEducation(id: number) {
    if (window.confirm("Are you sure you want to delete this record?")) {
      this.service.deleteEducation(id, Number(this.facilityId) || 0).subscribe(() => {
        this.loadEducations();
      });
    }
  }
}
